import React, { useMemo, useState } from "react";
import axios from "axios";
import { api } from "../../../config/api";
import { Select, Input, InputNumber, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";

const { TextArea } = Input;

const ControleCaixaTela = ({
  tipoMovimentacao = "ENTRADA",
  operacao,
  sessaoCaixa,
  onClose,
  onConfirm,
}) => {
  const [formaPagamento, setFormaPagamento] = useState("DINHEIRO");
  const [valor, setValor] = useState(null);
  const [observacao, setObservacao] = useState("");
  const [lancamentos, setLancamentos] = useState([]);
  const [confirmando, setConfirmando] = useState(false);

  const accessToken = window.localStorage.getItem("accessToken");

  const ehEntrada = tipoMovimentacao === "ENTRADA";

  const operacoesDisponiveis = useMemo(() => {
    const operacaoPadrao = ehEntrada ? "ENTRADA_INICIAL" : "SAQUE";

    const lista = Array.isArray(operacao)
      ? operacao
      : operacao
        ? [operacao]
        : [operacaoPadrao];

    return lista
      .map((item) => {
        if (typeof item === "string") {
          return {
            value: item,
            label: item
              .replace(/_/g, " ")
              .toLowerCase()
              .replace(/(^|\s)\S/g, (letra) => letra.toUpperCase()),
          };
        }

        return {
          value: item?.value,
          label:
            item?.label ||
            String(item?.value || "")
              .replace(/_/g, " ")
              .toLowerCase()
              .replace(/(^|\s)\S/g, (letra) => letra.toUpperCase()),
        };
      })
      .filter((item) => item.value);
  }, [operacao, ehEntrada]);

  const [operacaoSelecionada, setOperacaoSelecionada] = useState(() => {
    if (Array.isArray(operacao)) {
      const primeira = operacao[0];

      return typeof primeira === "string"
        ? primeira
        : primeira?.value;
    }

    return operacao || (ehEntrada ? "ENTRADA_INICIAL" : "SAQUE");
  });

  const mostrarComboOperacao = operacoesDisponiveis.length > 1;

  const totalMovimentado = useMemo(() => {
    return lancamentos.reduce(
      (total, item) => total + Number(item.valor || 0),
      0
    );
  }, [lancamentos]);

  const formatarMoeda = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });

  const adicionarLancamento = () => {
    const valorNumerico = Number(valor || 0);

    if (valorNumerico <= 0) {
      message.warning("Informe um valor válido.");
      return;
    }

    const operacaoDoLancamento =
      operacaoSelecionada ||
      operacoesDisponiveis[0]?.value;

    if (!operacaoDoLancamento) {
      message.warning("Selecione uma operação.");
      return;
    }

    const novoLancamento = {
      id: `${Date.now()}_${Math.random()}`,
      tipoMovimentacao,
      formaPagamento,
      operacao: operacaoDoLancamento,
      valor: valorNumerico,
    };

    setLancamentos((prev) => [...prev, novoLancamento]);

    setValor(null);
    setFormaPagamento("DINHEIRO");
    setOperacaoSelecionada(
      operacoesDisponiveis[0]?.value
    );
  };

  const removerLancamento = (id) => {
    setLancamentos((prev) =>
      prev.filter((item) => item.id !== id)
    );
  };

  const handleConfirmar = async () => {
    if (lancamentos.length === 0) {
      message.warning("Adicione ao menos um valor.");
      return;
    }

    if (!sessaoCaixa?.id) {
      message.error("Nenhuma sessão de caixa ativa foi identificada.");
      return;
    }

    const idLoja = sessaoCaixa?.idLoja;
    const idUsuario = sessaoCaixa?.idUsuario;

    if (!idLoja) {
      message.error("Não foi possível identificar a loja da sessão.");
      return;
    }

    if (!idUsuario) {
      message.error("Não foi possível identificar o usuário da sessão.");
      return;
    }

    const tipo = ehEntrada ? "E" : "S";

    const options = {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
      params: {
        idLoja,
        idUsuario,
      },
    };

    setConfirmando(true);

    try {
      const movimentacoesCriadas = await Promise.all(
        lancamentos.map(async (item) => {
          const payload = {
            idEventoRef: null,
            idMeioPagamentoRef: null,
            formaDePagamento: item.formaPagamento,
            tipo,
            valor: ehEntrada
              ? Number(item.valor)
              : Number(item.valor) * -1,
            operacao: item.operacao,
            observacao: observacao
          };

          const response = await axios.post(
            `${api.techspdv}/api/sessoes-caixa/${sessaoCaixa.id}/movimentacoes`,
            payload,
            options
          );

          return response.data;
        })
      );

      message.success(
        ehEntrada
          ? "Entrada registrada com sucesso!"
          : "Saída registrada com sucesso!"
      );

      onConfirm?.({
        tipoMovimentacao,
        operacoes: [
          ...new Set(
            lancamentos.map((item) => item.operacao)
          ),
        ],
        observacao,
        lancamentos,
        totalMovimentado,
        movimentacoes: movimentacoesCriadas,
      });

      setLancamentos([]);
      setValor(null);
      setFormaPagamento("DINHEIRO");
      setOperacaoSelecionada(
        operacoesDisponiveis[0]?.value
      );
      setObservacao("");

      onClose?.();
    } catch (error) {
      console.error("Erro ao registrar movimentação de caixa:", error);

      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Não foi possível registrar a movimentação de caixa."
      );
    } finally {
      setConfirmando(false);
    }
  };

  /*
   * ESTILOS
   */

  const estiloPagina = {
    width: "100%",
    padding: "12px",
    background: "#f4f6f9",
    boxSizing: "border-box",
  };

  const estiloCard = {
    width: "100%",
    maxWidth: "900px",
    margin: "0 auto",
    padding: "16px",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
    boxSizing: "border-box",
  };

  const estiloCabecalho = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    paddingBottom: "12px",
    marginBottom: "12px",
    borderBottom: "1px solid #e5e7eb",
  };

  const estiloTitulo = {
    margin: 0,
    color: "#111827",
    fontSize: "20px",
    fontWeight: "800",
  };

  const estiloSubtitulo = {
    display: "block",
    marginTop: "3px",
    color: "#6b7280",
    fontSize: "13px",
  };

  const estiloIconeCabecalho = {
    width: "44px",
    height: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "12px",

    background: ehEntrada
      ? "#dcfce7"
      : "#fee2e2",

    fontSize: "23px",
  };

  const estiloIndicadorTipo = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 14px",
    marginBottom: "12px",

    background: ehEntrada
      ? "#f0fdf4"
      : "#fef2f2",

    border: ehEntrada
      ? "1px solid #bbf7d0"
      : "1px solid #fecaca",

    borderRadius: "12px",
  };

  const estiloIndicadorIcone = {
    width: "38px",
    height: "38px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "10px",

    background: ehEntrada
      ? "#22c55e"
      : "#ef4444",

    color: "#fff",
    fontSize: "20px",
  };

  const estiloIndicadorTitulo = {
    color: ehEntrada
      ? "#166534"
      : "#991b1b",

    fontSize: "14px",
    fontWeight: "900",
  };

  const estiloIndicadorDescricao = {
    display: "block",
    marginTop: "2px",
    color: "#6b7280",
    fontSize: "12px",
  };

  const estiloSecao = {
    padding: "14px",
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    boxSizing: "border-box",
    marginBottom: "12px",
  };

  const estiloGridCampos = {
    display: "grid",
    gridTemplateColumns: mostrarComboOperacao
      ? "1.25fr 1.25fr 1fr auto"
      : "1.4fr 1fr auto",
    gap: "10px",
    alignItems: "end",
  };

  const estiloCampo = {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    minWidth: 0,
  };

  const estiloLabel = {
    color: "#374151",
    fontSize: "12px",
    fontWeight: "700",
  };

  const estiloBotaoAdicionar = {
    minHeight: "40px",
    padding: "8px 18px",
    border: "1px solid #000",
    borderRadius: "9px",

    background:
      "linear-gradient(135deg, #27272a, #000000)",

    color: "#fff",
    fontSize: "14px",
    fontWeight: "800",
    cursor: "pointer",

    boxShadow:
      "0 5px 12px rgba(0, 0, 0, 0.22)",
  };

  const estiloLista = {
    marginTop: "12px",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    overflow: "hidden",
  };

  const estiloCabecalhoLista = {
    display: "grid",
    gridTemplateColumns: mostrarComboOperacao
      ? "1fr 1fr 1fr 50px"
      : "1fr 1fr 50px",
    gap: "10px",

    padding: "10px 12px",

    background: "#f1f5f9",

    color: "#475569",
    fontSize: "12px",
    fontWeight: "800",

    borderBottom: "1px solid #e5e7eb",
  };

  const estiloLinhaLista = {
    display: "grid",
    gridTemplateColumns: mostrarComboOperacao
      ? "1fr 1fr 1fr 50px"
      : "1fr 1fr 50px",
    gap: "10px",

    padding: "10px 12px",
    alignItems: "center",

    borderBottom: "1px solid #f1f5f9",

    fontSize: "13px",
  };

  const estiloResumo = {
    display: "grid",
    gridTemplateColumns: "1.5fr 1fr",
    gap: "10px",
    marginTop: "12px",
  };

  const estiloCardObservacao = {
    padding: "12px",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
  };

  const estiloCardTotal = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    gap: "16px",

    padding: "14px 18px",

    background:
      "linear-gradient(135deg, #18181b, #000000)",

    borderRadius: "12px",

    color: "#ffffff",

    boxShadow:
      "0 7px 18px rgba(0, 0, 0, 0.2)",
  };

  const estiloLabelTotal = {
    color: "#d1d5db",
    fontSize: "12px",
    fontWeight: "700",
    textTransform: "uppercase",
  };

  const estiloValorTotal = {
    marginTop: "4px",
    color: "#ffffff",
    fontSize: "24px",
    fontWeight: "900",
  };

  const estiloRodape = {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",

    paddingTop: "12px",
    marginTop: "12px",

    borderTop: "1px solid #e5e7eb",
  };

  const estiloBotaoCancelar = {
    minWidth: "110px",
    minHeight: "42px",
    padding: "9px 18px",

    border: "1px solid #dc2626",
    borderRadius: "9px",

    background:
      "linear-gradient(135deg, #ef4444, #b91c1c)",

    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "800",
    cursor: "pointer",

    boxShadow:
      "0 5px 12px rgba(220, 38, 38, 0.20)",
  };

  const estiloBotaoConfirmar = {
    minWidth: "120px",
    minHeight: "42px",
    padding: "9px 18px",

    border: "1px solid #000000",
    borderRadius: "9px",

    background:
      "linear-gradient(135deg, #27272a, #000000)",

    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "800",
    cursor: "pointer",

    boxShadow:
      "0 5px 12px rgba(0, 0, 0, 0.22)",
  };

  return (
    <div style={estiloPagina}>
      <div style={estiloCard}>

        {/* CABEÇALHO */}
        <div style={estiloCabecalho}>
          <div>
            <h2 style={estiloTitulo}>
              Movimentações de caixa
            </h2>


          </div>

          <div style={estiloIconeCabecalho}>
            {ehEntrada ? "💵" : "💸"}
          </div>
        </div>

        {/* TIPO */}
        <div style={estiloIndicadorTipo}>
          <div style={estiloIndicadorIcone}>
            {ehEntrada ? "↓" : "↑"}
          </div>

          <div>
            <div style={estiloIndicadorTitulo}>
              {ehEntrada
                ? "Entrada de valores"
                : "Saída de valores"}
            </div>

            <span style={estiloIndicadorDescricao}>
              {ehEntrada
                ? "Os valores serão adicionados ao caixa."
                : "Os valores serão retirados do caixa."}
            </span>
          </div>
        </div>

        {/* FORMA + VALOR */}
        <div style={estiloSecao}>
          <div style={estiloGridCampos}>

            <div style={estiloCampo}>
              <label style={estiloLabel}>
                Forma de pagamento
              </label>

              <Select
                value={formaPagamento}
                onChange={setFormaPagamento}
                size="large"
                style={{ width: "100%" }}
              >
                <Select.Option value="DINHEIRO">
                  Dinheiro
                </Select.Option>

                <Select.Option value="PIX">
                  Pix
                </Select.Option>
              </Select>
            </div>

            {mostrarComboOperacao && (
              <div style={estiloCampo}>
                <label style={estiloLabel}>
                  Operação
                </label>

                <Select
                  value={operacaoSelecionada}
                  onChange={setOperacaoSelecionada}
                  size="large"
                  style={{ width: "100%" }}
                  placeholder="Selecione a operação"
                >
                  {operacoesDisponiveis.map((item) => (
                    <Select.Option
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            <div style={estiloCampo}>
              <label style={estiloLabel}>
                Valor
              </label>

              <InputNumber
                value={valor}
                onChange={setValor}
                size="large"
                min={0}
                precision={2}
                style={{
                  width: "100%",
                }}
                formatter={(value) => {
                  if (
                    value === undefined ||
                    value === null ||
                    value === ""
                  ) {
                    return "";
                  }

                  return `R$ ${value}`;
                }}
                parser={(value) =>
                  value
                    ?.replace("R$", "")
                    .replace(/\s/g, "")
                    .replace(",", ".")
                }
              />
            </div>

            <button
              type="button"
              style={estiloBotaoAdicionar}
              onClick={adicionarLancamento}
            >
              + Adicionar valor
            </button>

          </div>

          {/* LISTA */}
          {lancamentos.length > 0 && (
            <div style={estiloLista}>

              <div style={estiloCabecalhoLista}>
                <span>Forma de pagamento</span>
                {mostrarComboOperacao && (
                  <span>Operação</span>
                )}
                <span>Valor</span>
                <span></span>
              </div>

              {lancamentos.map((item) => (
                <div
                  key={item.id}
                  style={estiloLinhaLista}
                >
                  <strong>
                    {item.formaPagamento === "DINHEIRO"
                      ? "💵 Dinheiro"
                      : "⚡ Pix"}
                  </strong>

                  {mostrarComboOperacao && (
                    <strong>
                      {operacoesDisponiveis.find(
                        (op) => op.value === item.operacao
                      )?.label ||
                        String(item.operacao || "")
                          .replace(/_/g, " ")}
                    </strong>
                  )}

                  <strong>
                    {formatarMoeda(item.valor)}
                  </strong>

                  <button
                    type="button"
                    title="Remover"
                    onClick={() =>
                      removerLancamento(item.id)
                    }
                    style={{
                      width: "32px",
                      height: "32px",
                      border: "none",
                      borderRadius: "8px",
                      background: "#fee2e2",
                      color: "#dc2626",
                      cursor: "pointer",
                    }}
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              ))}

            </div>
          )}
        </div>

        {/* OBSERVAÇÃO + TOTAL */}
        <div style={estiloResumo}>
          {
          <div style={estiloCardObservacao}>
            <label
              style={{
                ...estiloLabel,
                display: "block",
                marginBottom: "7px",
              }}
            >
              Observação
            </label>

            <TextArea
              value={observacao}
              onChange={(e) =>
                setObservacao(e.target.value)
              }
              rows={3}
              placeholder={
                ehEntrada
                  ? "Ex.: Reforço de caixa..."
                  : "Ex.: Pagamento de fornecedor..."
              }
            />
          </div>
            }
          <div style={estiloCardTotal}>
            <div>
              <div style={estiloLabelTotal}>
                {ehEntrada
                  ? "Total da entrada"
                  : "Total da saída"}
              </div>

              <div style={estiloValorTotal}>
                {formatarMoeda(totalMovimentado)}
              </div>
            </div>

            <span style={{ fontSize: "28px" }}>
              {ehEntrada ? "💰" : "💸"}
            </span>
          </div>

        </div>

        {/* BOTÕES */}
        <div style={estiloRodape}>
          <button
            type="button"
            style={estiloBotaoCancelar}
            onClick={onClose}
          >
            Cancelar
          </button>

          <button
            type="button"
            style={{
              ...estiloBotaoConfirmar,
              opacity:
                lancamentos.length === 0 || confirmando
                  ? 0.5
                  : 1,

              cursor:
                lancamentos.length === 0 || confirmando
                  ? "not-allowed"
                  : "pointer",
            }}
            disabled={lancamentos.length === 0 || confirmando}
            onClick={handleConfirmar}
          >
            {confirmando ? "Salvando..." : "✓ Confirmar"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ControleCaixaTela;